/* SearchControlRenderer.js */

"use strict";

const _ = require("lodash");
const CheckboxControl = require("@gluenetworks/input_checkbox_control");
const CommonChevronIcon = require("../../icons/rawicons/chevron.icon.svg.js");
const InputSearchControl = require("@gluenetworks/input_search_control");

class SearchControlRenderer {
    constructor(model) {
        this._model = model;
        this._editor = model._editor;
        this._editorSession = model._editorSession;
        this._searchOptions = {
            backwards: true,
            wrap: true,
            caseSensitive: false,
            wholeWord: false,
            regExp: false,
            start: undefined,
            range: undefined,
        };

        this._onChangeSelectionDebounce = _.debounce(this._onChangeSelection.bind(this), 25);
        this._onChangeCursorDebounce = _.debounce(this._onChangeCursor.bind(this), 100);
        this._onChangeScrollTopDebounce = _.debounce(this._onChangeScrollTop.bind(this), 100);
        this._onEditorInputDebounce = _.debounce(this._onEditorInput.bind(this), 100);
        this._searchStringMarkers = [];
        this._cursorPositionChanged = false;

        if (this._model.buildActionBar) {
            this._rootElement = document.createElement("span");
            this._rootElement.classList.add("search-root");
            this._inputSearchControl = new InputSearchControl();

            this._findPreviousButton = document.createElement("button");
            this._findPreviousButton.classList.add("find-previous");
            this._findPreviousButton.innerHTML = CommonChevronIcon.icon;
            this._findPreviousButton.title = "Find Previous";
            this._findNextButton = document.createElement("button");
            this._findNextButton.classList.add("find-next");
            this._findNextButton.innerHTML = CommonChevronIcon.icon;
            this._findNextButton.title = "Find Next";
            this._searchCountDetails = document.createElement("span");
            this._searchCountDetails.classList.add("search_count_details");

            this._caseSensitiveCheckbox = new CheckboxControl({
                label: "Case-Sensitive",
                labelShown: true,
                borderless: false,
            });
            this._caseSensitiveCheckbox.renderer.rootElement.classList.add("search_case_sensitive");
        }

        this._registerListeners();
        this.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /*******************************************/
    _registerListeners() {
        this._model.on("searchString", (this._onSearchString = this._onSearchString.bind(this)));
        this._model.on(
            "searchCaseSensitive",
            (this._onSearchCaseSensitive = this._onSearchCaseSensitive.bind(this))
        );
        this._model.on(
            "highlightNextSearchStringMatch",
            (this._onHighlightNextSearchStringMatch =
                this._onHighlightNextSearchStringMatch.bind(this))
        );
        this._model.on(
            "highlightPreviousSearchStringMatch",
            (this._onHighlightPreviousSearchStringMatch =
                this._onHighlightPreviousSearchStringMatch.bind(this))
        );
        if (this._model.buildActionBar) {
            this._inputSearchControl.on(
                "UserValue",
                (this._onSearchCriteriaInputDebounce = _.debounce(
                    this._onSearchCriteriaInput.bind(this),
                    500
                ))
            );
            this._findNextButton.addEventListener(
                "click",
                (this._onFindNextButtonClick = this._onFindNextButtonClick.bind(this))
            );
            this._findPreviousButton.addEventListener(
                "click",
                (this._onFindPreviousButtonClick = this._onFindPreviousButtonClick.bind(this))
            );
            this._caseSensitiveCheckbox.on(
                "UserValue",
                (this._onSearchButtonClick = this._onSearchButtonClick.bind(this))
            );
        }
        this._editorSession.selection.on("changeSelection", this._onChangeSelectionDebounce);
        this._editor.on("input", this._onEditorInputDebounce);
    }

    _unregisterListeners() {
        this._model.removeListener("searchString", this._onSearchString);
        this._model.removeListener("searchCaseSensitive", this._onSearchCaseSensitive);
        if (this._model.buildActionBar) {
            this._inputSearchControl.removeListener(
                "UserValue",
                this._onSearchCriteriaInputDebounce
            );
            this._findNextButton.removeEventListener("click", this._onFindNextButtonClick);
            this._findPreviousButton.removeEventListener("click", this._onFindPreviousButtonClick);
            this._caseSensitiveCheckbox.removeListener("UserValue", this._onSearchButtonClick);
        }

        this._editorSession.selection.removeEventListener(
            "changeSelection",
            this._onChangeSelectionDebounce
        );
        this._editorSession.removeEventListener("click", this._onChangeCursorDebounce);
        this._editorSession.removeEventListener("dblclick", this._onChangeCursorDebounce);
        this._editorSession.removeEventListener("changeScrollTop", this._onChangeScrollTopDebounce);
        this._editor.removeEventListener("input", this._onEditorInputDebounce);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._editor = undefined;
        this._editorSession = undefined;
        this._onChangeScrollTopDebounce = undefined;
        this._onChangeSelectionDebounce = undefined;
        this._onChangeCursorDebounce = undefined;
        this._onEditorInputDebounce = undefined;
        this._onSearchCriteriaInputDebounce = undefined;
        this._textSelectionMarker = undefined;
        this._searchStringMarkers = undefined;
        this._currentSearchStringNumber = undefined;
        this._searchStringMatchCount = undefined;
        this._cursorPositionChanged = undefined;

        if (this._model.buildActionBar) {
            return this._inputSearchControl.shutdown().then(() => {
                this._inputSearchControl = undefined;
                this._findNextButton = undefined;
                this._findPreviousButton = undefined;
                this._caseSensitiveCheckbox = undefined;
                this._findNextButton = undefined;
                this._searchCountDetails = undefined;
                this._rootElement.remove();
                this._rootElement = undefined;
            });
        } else {
            return Promise.resolve();
        }
    }

    render() {
        if (this._model.buildActionBar) {
            this._rootElement.appendChild(this._inputSearchControl.renderer.rootElement);
            this._rootElement.appendChild(this._findPreviousButton);
            this._rootElement.appendChild(this._searchCountDetails);
            this._rootElement.appendChild(this._findNextButton);
            this._rootElement.appendChild(this._caseSensitiveCheckbox.renderer.rootElement);
        }

        this._setSearchString(this._model.searchString);
        setTimeout(() => {
            this._setSearchCaseSensitive(this._model.searchCaseSensitive);
        }, 10);
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElement;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    getSearchStringMatchCount() {
        return this._searchStringMatchCount;
    }

    getCurrentMatchSequenceNumber() {
        return this._searchStringMatchCount > 0 ? this._currentSearchStringNumber : undefined;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    /**
     * Fired when the search string has been updated in the model
     * @private
     */
    _onSearchString() {
        this._setSearchString(this._model.searchString); // update input search box text, run search for text string
    }

    _onSearchCaseSensitive() {
        this._setSearchCaseSensitive(this._model.searchCaseSensitive);
    }

    _onHighlightPreviousSearchStringMatch() {
        this._findPreviousSearchString();
    }

    _onHighlightNextSearchStringMatch() {
        this._findNextSearchString();
    }

    /**
     * Fired when find next button is clicked
     * @private
     */
    _onFindNextButtonClick() {
        this._findNextSearchString();
    }

    /**
     * Fired when find previous button is clicked
     * @private
     */
    _onFindPreviousButtonClick() {
        this._findPreviousSearchString();
    }

    _onSearchButtonClick() {
        this._model.setSearchCaseSensitive(this._caseSensitiveCheckbox.value, true);
    }

    _onChangeSelection() {
        let currentSelectionRange = this._editor.getSelectionRange();
        if (!_.isEmpty(this._textSelectionMarker)) {
            let previousSelectionMarkers = this._textSelectionMarker;
            let previousSelectionMarkerRange =
                this._editorSession.getMarkers(true)[previousSelectionMarkers[0]].range;
            // remove all markers in the array
            this._textSelectionMarker.forEach((marker) => {
                this._editorSession.removeMarker(marker);
            });
            this._textSelectionMarker = [];
            if (
                !_.isEmpty(this._searchStringMarkers) &&
                this._isEqualTo(
                    this._model.searchString,
                    this._editor.getSelectedText(),
                    this._model.searchCaseSensitive
                )
            ) {
                // add gray marker in place of previous selection marker
                if (
                    previousSelectionMarkers.length === 1 &&
                    this._isResponseRow(previousSelectionMarkerRange.start.row)
                ) {
                    this._searchStringMarkers.push(
                        this._editorSession.addMarker(
                            previousSelectionMarkerRange,
                            "search_string_marker",
                            this._searchStringMarkerRenderer.bind(this),
                            true
                        )
                    );
                }

                //add marker on new selection text
                this._textSelectionMarker = [
                    this._editorSession.addMarker(
                        currentSelectionRange,
                        "selection_marker",
                        this._selectionMarkerRenderer.bind(this),
                        true
                    ),
                ];
                if (this._isResponseRow(currentSelectionRange.start.row)) {
                    // remove gray marker on new selection text
                    let currentSelectionMarkerIndex = this._searchStringMarkers.findIndex(
                        (markerId) => {
                            return this._editorSession
                                .getMarkers(true)
                                [markerId].range.isEqual(currentSelectionRange);
                        },
                        this
                    );
                    if (currentSelectionMarkerIndex >= 0) {
                        this._editorSession.removeMarker(
                            this._searchStringMarkers[currentSelectionMarkerIndex]
                        );
                        this._searchStringMarkers.splice(currentSelectionMarkerIndex, 1);
                    }
                }
                return;
            }
        }
        this._textSelectionMarker = this._addSelectionMarker(
            currentSelectionRange,
            "selection_marker"
        );
    }

    /**
     * Fired when search input box content is changed
     * @private
     */
    _onSearchCriteriaInput(newVal) {
        this._model.setSearchString(newVal, true);
    }

    _onChangeScrollTop() {
        this._updateSearchMarkers();
    }

    _onChangeCursor() {
        this._cursorPositionChanged = true;
    }

    _onEditorInput() {
        let scrollTopBeforeAppendingLogs = this._editorSession.getScrollTop();
        let scrollLeftBeforeAppendingLogs = this._editorSession.getScrollLeft();
        this._searchEditor(this._model.searchString, this._model.searchCaseSensitive, true);
        if (this._model.scrollLock) {
            this._editorSession.setScrollTop(scrollTopBeforeAppendingLogs);
            this._editorSession.setScrollLeft(scrollLeftBeforeAppendingLogs);
        } else {
            this._model.scrollToBottom();
        }
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */

    /*******************************************/

    _setSearchString(searchString) {
        if (this._model.buildActionBar && this._inputSearchControl.value !== searchString) {
            this._inputSearchControl.value = searchString;
        }
        this._searchEditor(searchString, this._model.searchCaseSensitive);
    }

    _setSearchCaseSensitive(searchCaseSensitive) {
        if (this._model.buildActionBar) {
            this._caseSensitiveCheckbox.value = searchCaseSensitive;
            this._caseSensitiveCheckbox.title = searchCaseSensitive
                ? "Case sensitive search"
                : "Ignore Case in search";
            this._searchOptions.caseSensitive = this._model.searchCaseSensitive;
        }
        this._searchEditor(this._model.searchString, this._model.searchCaseSensitive);
    }

    _searchEditor(searchText, isSearchCaseSensitive) {
        this._model.searchString = searchText;
        this._cursorPositionChanged = false;

        this._searchOptions.caseSensitive = isSearchCaseSensitive;
        let selectionRangeBeforeSearch = this._editor.getSelectionRange();
        let searchOptions = _.clone(this._searchOptions);
        searchOptions.start = {row: 0, column: 0};
        searchOptions.needle = searchText;
        searchOptions.wrap = true;
        searchOptions.backwards = true;
        let oldSearchStringMatchCount = this._searchStringMatchCount;
        this._searchStringMatchCount = this._editor.findAll(searchText, searchOptions);
        searchOptions.start = selectionRangeBeforeSearch.start;
        searchOptions.range = undefined;
        this._editor.find(searchText, searchOptions);
        this._updateCurrentMatchSequenceNumber();
        this._model._triggerSearchResultCountChanged(
            this._searchStringMatchCount,
            oldSearchStringMatchCount
        );
        this._removeClassesFromRootElement(["zero-search-results", "search-results"]);
        if (searchText) {
            let searchHighlightOptions = _.clone(this._editor.$search.$options.re);
            searchHighlightOptions.start = {row: 0, column: 0};
            this._editorSession.highlight(searchHighlightOptions);
            this._updateSearchMarkers();
            let className =
                this._searchStringMatchCount === 0 ? "zero-search-results" : "search-results";
            this._addClassToRootElement(className);
            if (this._searchStringMatchCount > 0) {
                this._editorSession.on("changeScrollTop", this._onChangeScrollTopDebounce);
                this._editor.on("click", this._onChangeCursorDebounce);
                this._editor.on("dblclick", this._onChangeCursorDebounce);
                this._findNextSearchString();
            } else {
                this._updateSearchCountDetails();
            }
        } else {
            this._editorSession.removeEventListener(
                "changeScrollTop",
                this._onChangeScrollTopDebounce
            );
            this._editor.removeEventListener("click", this._onChangeCursorDebounce);
            this._editor.removeEventListener("dblclick", this._onChangeCursorDebounce);
            this._deleteSearchMarkers(this._searchStringMarkers);
            if (this._searchCountDetails) {
                this._searchCountDetails.innerText = "";
            }
        }
    }

    _findNextSearchString() {
        let selectedText = this._editor.getSelectedText();
        let oldSearchStringNumber = this._currentSearchStringNumber;
        if (
            !selectedText ||
            this._isEqualTo(selectedText, this._model.searchString, this._model.searchCaseSensitive)
        ) {
            let previousSelectionRange = this._editor.getSelectionRange();
            this._editor.findNext();
            this._editorSession.highlight(this._editor.$search.$options.re);
            if (this._searchStringMatchCount > 1) {
                if (this._cursorPositionChanged) {
                    this._updateCurrentMatchSequenceNumber();
                } else {
                    let currentSelectionRange = this._editor.getSelectionRange();
                    this._currentSearchStringNumber =
                        currentSelectionRange.compareStart(
                            previousSelectionRange.start.row,
                            previousSelectionRange.start.column
                        ) === 1
                            ? 1
                            : this._currentSearchStringNumber + 1;
                }
            }
            this._updateSearchCountDetails();
            this._model._triggerCurrentSearchStringSequenceNumberChanged(
                this._currentSearchStringNumber,
                oldSearchStringNumber
            );
            this._cursorPositionChanged = false;
        } else {
            this._searchEditor(this._model.searchString, this._model.searchCaseSensitive);
        }
    }

    _findPreviousSearchString() {
        let selectedText = this._editor.getSelectedText();
        let oldSearchStringNumber = this._currentSearchStringNumber;
        if (
            !selectedText ||
            this._isEqualTo(selectedText, this._model.searchString, this._model.searchCaseSensitive)
        ) {
            let previousSelectionRange = this._editor.getSelectionRange();
            this._editor.findPrevious();
            this._editorSession.highlight(this._editor.$search.$options.re);
            if (this._searchStringMatchCount > 1) {
                if (this._cursorPositionChanged) {
                    this._updateCurrentMatchSequenceNumber();
                } else {
                    let currentSelectionRange = this._editor.getSelectionRange();
                    this._currentSearchStringNumber =
                        currentSelectionRange.compareStart(
                            previousSelectionRange.start.row,
                            previousSelectionRange.start.column
                        ) === -1
                            ? this._searchStringMatchCount
                            : this._currentSearchStringNumber - 1;
                }
            }
            this._updateSearchCountDetails();
            this._model._triggerCurrentSearchStringSequenceNumberChanged(
                this._currentSearchStringNumber,
                oldSearchStringNumber
            );
            this._cursorPositionChanged = false;
        } else {
            this._searchEditor(this._model.searchString, this._model.searchCaseSensitive);
        }
    }

    _updateSearchMarkers() {
        //delete existing search markers
        this._deleteSearchMarkers(this._searchStringMarkers);
        let firstVisibleRow = this._editor.getFirstVisibleRow();
        let lastVisibleRow = this._editor.getLastVisibleRow();
        let regexSearchFlags = "g" + (this._model.searchCaseSensitive ? "" : "i");
        for (let row = firstVisibleRow; row <= lastVisibleRow; row++) {
            let currentRowText = this._editorSession.getLine(row);
            if (this._isResponseRow(row)) {
                let regex = new RegExp(this._model.searchString, regexSearchFlags);
                let match = regex.exec(currentRowText);
                while (!_.isNil(match) && !_.isEmpty(match[0])) {
                    let Range = ace.require("ace/range").Range;
                    let markerRange = new Range(
                        row,
                        match.index,
                        row,
                        match.index + match[0].length
                    );
                    let markerId = this._editorSession.addMarker(
                        markerRange,
                        "search_string_marker",
                        this._searchStringMarkerRenderer.bind(this),
                        true
                    );
                    this._searchStringMarkers.push(markerId);
                    match = regex.exec(currentRowText);
                }
            }
        }
        this._onChangeSelection();
    }

    _deleteSearchMarkers(markers) {
        while (markers.length > 0) {
            let currentMarker = markers.pop();
            this._editorSession.removeMarker(currentMarker);
        }
    }

    _addSelectionMarker(range) {
        let splitRangeDetails = this._splitMultiLineRange(range, this._editorSession);
        return splitRangeDetails.map((splitRangeDetail) => {
            let renderer =
                splitRangeDetail.type === "partial_line"
                    ? this._selectionMarkerRenderer.bind(this)
                    : this._selectionMarkerFullLineRenderer.bind(this);
            return this._editorSession.addMarker(splitRangeDetail.range, "", renderer, true);
        });
    }

    _updateCurrentMatchSequenceNumber() {
        if (this._searchStringMatchCount > 1) {
            let currentSelectionRange = this._editor.getSelectionRange();
            let Range = ace.require("ace/range").Range;
            let range = new Range(
                0,
                0,
                currentSelectionRange.end.row,
                currentSelectionRange.end.column
            );

            this._currentSearchStringNumber = this._getMatchesInGivenRange(
                this._model.searchString,
                range,
                this._editorSession,
                this._model.searchCaseSensitive
            );
        } else {
            this._currentSearchStringNumber = 1;
        }
    }

    _isEqualTo(string1, string2, caseSensitive) {
        return caseSensitive
            ? string1 === string2
            : string1.toUpperCase() === string2.toUpperCase();
    }

    _selectionMarkerRenderer(html, range, left, top, config) {
        let text = this._getTextFromScreenRange(range);
        let rows =
            range.end.column === 0
                ? range.end.row - range.start.row
                : range.end.row - range.start.row + 1;
        let style = this._getMarkerStyle(text, left, top, config, rows);
        html.push('<div class="selection_marker" style="' + style + '">' + text + "</div>");
        return html;
    }

    _searchStringMarkerRenderer(html, range, left, top, config) {
        let text = this._getTextFromScreenRange(range);
        let style = this._getMarkerStyle(text, left, top, config);
        html.push('<div class="search_string_marker" style="' + style + '">' + text + "</div>");
        return html;
    }

    _selectionMarkerFullLineRenderer(html, range, left, top, config) {
        let text = this._getTextFromScreenRange(range);
        let rows =
            range.end.column === 0
                ? range.end.row - range.start.row
                : range.end.row - range.start.row + 1;
        if (
            range.isMultiLine() &&
            range.start.column >=
                this._editorSession.getLine(
                    this._editorSession.screenToDocumentRow(range.start.row, range.start.column)
                ).length
        ) {
            rows = rows - 1;
            top = top + config.lineHeight;
            left = 4;
        }
        let height = config.lineHeight * rows;
        let style =
            "height: " +
            height +
            "px;" +
            "top: " +
            top +
            "px; left: " +
            left +
            "px;" +
            "right: 0px;";
        html.push(
            '<div class="selection_marker marker_full_line" style="' +
                style +
                '">' +
                text +
                "</div>"
        );
        return html;
    }

    _getMarkerStyle(text, left, top, config, rows = 1) {
        let width = text.length * config.characterWidth;
        let height = config.lineHeight * rows;
        return (
            "height: " +
            height +
            "px;" +
            "width: " +
            width +
            "px;" +
            "top: " +
            top +
            "px; left: " +
            left +
            "px;"
        );
    }

    /**
     * Given a screen range, returns visible text in the screen range (avoiding text hidden in code folds)
     * @param range - screen range of marker
     * @returns {*} - text shown on the editor in given screen range
     * @private
     */
    _getTextFromScreenRange(range) {
        let Range = ace.require("ace/range").Range;
        let rangeStartDocPosition = this._editorSession.screenToDocumentPosition(
            range.start.row,
            range.start.column
        );
        let rangeEndDocPosition = this._editorSession.screenToDocumentPosition(
            range.end.row,
            range.end.column
        );
        let documentRange = new Range(
            rangeStartDocPosition.row,
            rangeStartDocPosition.column,
            rangeEndDocPosition.row,
            rangeEndDocPosition.column
        );

        let folds = this._editorSession.getFoldsInRange(documentRange);
        // no folds in range, get text from doc range
        if (_.isEmpty(folds)) {
            return this._editorSession.getTextRange(documentRange);
        } else if (!range.isMultiLine()) {
            // if screen range is single line & no folds in range, return text on first line of screen range
            let firstLineRange = !documentRange.isMultiLine()
                ? this._editorSession.getTextRange(documentRange)
                : {
                      start: documentRange.start,
                      end: {
                          row: documentRange.start.row,
                          column: this._editorSession.getLine(documentRange.start.row).length,
                      },
                  };
            return this._editorSession.getTextRange(firstLineRange);
        }

        // in case of folds in doc range, calculate marker text by avoiding hidden lines in folds & adding text in visible lines
        let text = "";
        let startRow = documentRange.start.row;
        if (documentRange.start.column !== 0) {
            startRow++;
        }
        for (let i = startRow; i <= documentRange.end.row; i++) {
            let isRowFolded = folds.some(function (fold) {
                return fold.range.insideEnd(i, 0);
            });
            text += isRowFolded ? "" : this._editorSession.getLine(i) + "\n";
        }
        return text;
    }

    _isResponseRow(row) {
        return "resp" === _.get(this._editorSession.getTokenAt(row), "type", "");
    }

    /**
     * splits multi line ranges into ranges where markers can be added individually
     * If first line of the range doesn't start at column 0, adds a range for this line
     * If last line of the range doesn't cover the entire line, adds a range for this line
     * Adds one more range out of input range which is not covered by above ranges
     * returns array of ranges
     * @private
     */
    _splitMultiLineRange(range, editorSession) {
        let splitRanges = [];
        if (!range || range.isEmpty()) {
            return [];
        }
        if (!range.isMultiLine()) {
            return [{range: range, type: "partial_line"}];
        }
        let blockStartRow = range.start.row;
        let blockEndRow = range.end.row;
        let blockMarkerRequired = false;
        let Range = ace.require("ace/range").Range;
        if (range.start.column === 0) {
            blockMarkerRequired = true;
        } else {
            let lineLength = editorSession.getLine(range.start.row).length + 1;
            let startLineRange = new Range(
                range.start.row,
                range.start.column,
                range.start.row,
                lineLength
            );
            splitRanges.push({range: startLineRange, type: "end_of_line"});
            blockStartRow = range.start.row + 1;
        }
        let lastLineRange = new Range(range.end.row, 0, range.end.row, range.end.column);
        splitRanges.push({range: lastLineRange, type: "partial_line"});
        blockEndRow = range.end.row - 1;
        if (range.start.row + 1 !== range.end.row || blockMarkerRequired) {
            let blockRangeLastLine = editorSession.getLine(blockEndRow);
            let blockRange = new Range(blockStartRow, 0, blockEndRow, blockRangeLastLine.length);
            splitRanges.push({range: blockRange, type: "block"});
        }
        return splitRanges;
    }

    _getMatchesInGivenRange(searchString, searchRange, editorSession, caseSensitive = false) {
        if (!searchRange || searchRange.isEmpty()) {
            return 0;
        }
        let Search = ace.require("ace/search").Search;
        let search = new Search();
        let options = {
            backwards: false,
            wrap: false,
            caseSensitive: caseSensitive,
            wholeWord: false,
            regExp: false,
            start: null,
            needle: searchString,
            skipCurrent: false,
        };
        search.setOptions(options);
        if (searchRange.isMultiLine()) {
            let subSearchRange = searchRange.clone(searchRange);
            subSearchRange.end = {row: searchRange.end.row, column: 0};
            search.set({range: subSearchRange});
            let matchRanges = search.findAll(editorSession);
            let count = matchRanges.length;
            if (matchRanges[matchRanges.length - 1].start.row === searchRange.end.row) {
                count--;
            }
            if (!searchRange.isEqual(subSearchRange)) {
                subSearchRange = {
                    start: {row: subSearchRange.end.row, column: 0},
                    end: {
                        row: searchRange.end.row,
                        column: editorSession.getLine(searchRange.end.row).length + 1,
                    },
                };
                search.$options.range = subSearchRange;
                let matchRangesOnLastRow = search.findAll(editorSession);
                let matchesInSearchRangeLastRow = matchRangesOnLastRow.filter(function (range) {
                    return range.start.column < this.column && range.start.row === this.row;
                }, searchRange.end);
                count += _.get(matchesInSearchRangeLastRow, "length", 0);
            }
            return count;
        } else {
            let searchRangeModified =
                searchRange.start.row === 0
                    ? {start: searchRange.start, end: {row: searchRange.end.row + 1, column: 0}}
                    : searchRange;
            search.set({range: searchRangeModified});
            let matchRanges = search.findAll(editorSession);
            let matchesInSearchRange = matchRanges.filter(function (range) {
                return range.start.column < this.column && range.start.row === this.row;
            }, searchRange.end);
            return matchesInSearchRange.length;
        }
    }

    _updateSearchCountDetails() {
        if (this._model.buildActionBar) {
            if (this._searchStringMatchCount > 0) {
                this._searchCountDetails.innerText =
                    this._currentSearchStringNumber + "/" + this._searchStringMatchCount;
                this._searchCountDetails.style["min-width"] =
                    ((this._searchStringMatchCount + "").length * 2 + 1) * 0.633 + 0.4 + "rem";
            } else {
                this._searchCountDetails.innerText = "No matches";
            }
        }
    }

    _addClassToRootElement(className) {
        if (this._rootElement) {
            this._rootElement.classList.add(className);
        }
    }

    _removeClassesFromRootElement(classes) {
        if (this._rootElement) {
            _.forEach(
                classes,
                (className) => {
                    this._rootElement.classList.remove(className);
                },
                this
            );
        }
    }
    /*******************************************/
    /* END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = SearchControlRenderer;
